import React from 'react';

import PropTypes from 'prop-types';

import { DownloadTheApp, TrustpilotReviews } from 'wanderu-app-gettheapp';
import {
    Control,
    SplitTest,
    Variant,
} from 'wanderu-component-split-test';
import {
    BackToTopButton,
    FAQSection,
    LongFormContent,
    ValueStatement,
} from 'wanderu-components';
import {
    carrierGuideShapes as shapes,
    CG_NAV_ITEMS,
    CG_POLICY_CONTENT,
    CG_CARRIER_CONTENT,
} from 'wanderu-ducks-seo';
import { localize } from 'wanderu-i18n';
import { getCountryFromLocale } from 'wanderu-utils';

import cgConfig from '../../../config/carrierguide.locale';
import AboutWanderu from '../AboutWanderu/AboutWanderu';
import CarrierReviews from '../CarrierReviews/SimplifiedCarrierReviews';
import EditorialBlurbs from '../EditorialBlurbs/EditorialBlurbs';
import GeneralSection from '../GeneralSection/GeneralSection';
import InfoSection from '../InfoSection/InfoSection';
import LinkSection from '../LinkSection/LinkSection';
import LinkTable from '../LinkTable/LinkTable';
import PromoBox from '../PromoBox/PromoBox';
import SEOModal from '../SEOModal/SEOModal';
import TableList from '../TableList/TableList';
import TableOfContentsSection from '../TableOfContents/TableOfContents';
import AboutCarrier from './AboutCarrier/AboutCarrier';
import CarrierBest from './CarrierBest/CarrierBest';
import CarrierGuideHeader from './CarrierGuideHeader/CarrierGuideHeader';
import CarrierQuickview from './CarrierQuickview/CarrierQuickview';
import RouteMap from './RouteMap/RouteMap';
import TopRoutes from './TopRoutes/TopRoutes';

import styles from './CarrierGuide.scss';

function generateNavItemsList({
    showReviewNav,
    showCheapTripNav,
    showCancellationNav,
    showTopRoutesNav,
    showRelatedReadingsNav,
}) {
    const navItems = [];

    navItems.push(CG_NAV_ITEMS.aboutNav);
    navItems.push(CG_NAV_ITEMS.bestNav);

    if (showReviewNav) {
        navItems.push(CG_NAV_ITEMS.reviewNav);
    }

    navItems.push(CG_NAV_ITEMS.mapNav);

    if (showTopRoutesNav) {
        navItems.push(CG_NAV_ITEMS.popularTripNav);
    }

    if (showCancellationNav) {
        navItems.push(CG_NAV_ITEMS.cancellationNav);
    }

    navItems.push(CG_NAV_ITEMS.faqNav);

    if (showCheapTripNav) {
        navItems.push(CG_NAV_ITEMS.cheapTripNav);
    }

    if (showRelatedReadingsNav) {
        navItems.push(CG_NAV_ITEMS.relatedReadingsNav);
    }

    return navItems;
}

// eslint-disable-next-line default-param-last
function getCarrierPhone(phoneObj = {}, locale) {
    const countryCode = getCountryFromLocale(locale).toUpperCase();

    if (phoneObj[countryCode]) {
        return phoneObj[countryCode];
    }

    return phoneObj[Object.keys(phoneObj)[0]];
}

function CarrierGuide(props) {
    const {
        bestFeatures = {},
        blurbSectionContent = {},
        carrier,
        carrierReviews,
        content,
        infoSectionContent,
        interlinks,
        locale,
        longFormContent,
        quickviewMetrics,
        reviewSummary,
        showModal = false,
        stats,
        t,
        topRoutes = [],
        travelReviews,
    } = props;
    const {
        activeStatus,
        contact: carrierContact,
        id,
        images,
        vehicleTypes,
    } = carrier;
    // Temporarily hidden while blog posts migrate from WordPress to nexus
    // (nexus requires post tagging that is not yet in place). This forces
    // the blog section and its "related readings" nav item off regardless
    // of renderFlags.showBlogBlurbSection. Re-enable by restoring the
    // renderFlags destructure. See wanderu/ui-react#1070.
    const showBlogBlurbSection = false;

    const showCarrierReviews = !!Object.keys(carrierReviews).length;
    const showCheapestRoutes = !!stats?.cheapest?.routes;
    const hasInterlinks = interlinks.carriers || interlinks.c2cs || interlinks.cities;
    const showCancellationPolicy = !!content?.cancellationPolicyTitle
        && !!content?.cancellationPolicyContent;
    const showTopRoutes = !!topRoutes.length;

    const navItems = generateNavItemsList({
        showCancellationNav: showCancellationPolicy,
        showCheapTripNav: showCheapestRoutes,
        showRelatedReadingsNav: showBlogBlurbSection,
        showReviewNav: showCarrierReviews,
        showTopRoutesNav: showTopRoutes,
    });

    return (
        <>
            <CarrierGuideHeader
                carrierActiveStatus={activeStatus}
                id={id}
                travelMode={vehicleTypes && vehicleTypes.primary}
            />
            <SplitTest testName="adtDiscount">
                <Control />
                <Variant name="group_a">
                    <PromoBox locale={locale} />
                </Variant>
            </SplitTest>
            <TableOfContentsSection
                className={styles.tocSection}
                namespace={cgConfig.namespace}
                navItems={navItems}
            />
            <AboutWanderu
                carrier={carrier}
                description={t('partnerInfoText')}
                title={t('partnerText')}
            />
            <TrustpilotReviews locale={locale} />
            <DownloadTheApp campaign="web-carrier_guide" />
            <AboutCarrier
                awardWinningCategories={content?.awardWinningCategories}
                carrier={carrier}
                images={images}
                stats={stats}
            />
            {showModal && (
                <SEOModal
                    bottomButtonLink={t('ctaLink2')}
                    bottomButtonText={t('ctaText2')}
                    modalText={t('modalText')}
                    topButtonLink={t('ctaLink1')}
                    topButtonText={t('ctaText1')}
                />
            )}
            {carrier.id && quickviewMetrics
                && <CarrierQuickview
                    carrier={carrier}
                    metrics={quickviewMetrics}
                />}
            <CarrierBest
                features={bestFeatures}
            />
            {showCarrierReviews && (
                <CarrierReviews
                    carrierReviews={carrierReviews}
                    carrierStats={travelReviews.stats}
                    namespace={cgConfig.namespace}
                    reviewSummary={reviewSummary}
                />
            )}
            <RouteMap
                metrics={stats?.metrics}
                topRoutes={stats?.top}
                showCities
            />
            {!!longFormContent.sections && !!longFormContent.sections.length
                && <LongFormContent
                    {...longFormContent}
                    namespace={cgConfig.namespace}
                    useSEOStyles
                />}
            {showTopRoutes && (
                <TopRoutes
                    topRoutes={topRoutes}
                />
            )}
            {showCancellationPolicy && (
                <GeneralSection
                    className={styles.cancellationPolicyTitle}
                    description={t('cancellationPolicyContent', {
                        carrierEmail: carrierContact?.email,
                        carrierPhone: getCarrierPhone(carrierContact?.phone, locale),
                    })}
                    id="carrier-cancellation"
                    title={t('cancellationPolicyTitle')}
                    show
                />
            )}
            {CG_POLICY_CONTENT.map(({ title, description }) => (
                <GeneralSection
                    description={t(description)}
                    id={title}
                    key={title}
                    show={t(title) !== title && t(description) !== description}
                    title={t(title)}
                />
            ))}
            <FAQSection title={t('faqHeader')}>
                {infoSectionContent.map((info) => (
                    <InfoSection
                        id={info.prefix}
                        infoText={info.text}
                        infoTitle={info.title}
                        key={info.prefix}
                    />
                ))}
            </FAQSection>
            {showCheapestRoutes && (
                <TableList
                    c2cRoutes={stats.cheapest.routes}
                    locale={locale}
                    minPrice={stats.pricing.minUsd}
                    cheapest
                />
            )}
            {CG_CARRIER_CONTENT.map(({ title, description }) => (
                <GeneralSection
                    description={t(description)}
                    id={title}
                    key={title}
                    show={t(title) !== title && t(description) !== description}
                    title={t(title)}
                />
            ))}
            {hasInterlinks && (
                <LinkSection className={styles.linkSection}>
                    {interlinks.carriers && (
                        <LinkTable
                            columns={2}
                            data={interlinks.carriers.map((data) => ({
                                carrierCode: data.carrierCode,
                                carrierName: data.carrierName,
                                linkAnchorText: data.carrierName,
                                linkUrl: data.linkUrl,
                            }))}
                            title={t('interlinkToCarrierTitle')}
                            showLinkBox
                        />
                    )}
                    {interlinks.c2cs && (
                        <LinkTable
                            data={interlinks.c2cs}
                            title={t('interlinkToC2cTitle')}
                            native
                        />
                    )}
                    {interlinks.cities && (
                        <LinkTable
                            data={interlinks.cities}
                            title={t('interlinkToCityTitle')}
                        />
                    )}
                </LinkSection>
            )}
            {showBlogBlurbSection && (
                <EditorialBlurbs
                    blurbContent={blurbSectionContent}
                    label="blog-section"
                    namespace={cgConfig.namespace}
                    title={t('blogTitle')}
                    variant="blog"
                    showDepartSection
                />
            )}
            <ValueStatement namespace={cgConfig.namespace} />
            <BackToTopButton />
        </>
    );
}

const imageShape = PropTypes.shape({
    metadata: PropTypes.shape({
        alt: PropTypes.string,
        author: PropTypes.string,
        license: PropTypes.string,
    }),
    name: PropTypes.string,
    size: PropTypes.number,
    src: PropTypes.string,
});

const stationShape = PropTypes.shape({
    code: PropTypes.string,
    images: PropTypes.arrayOf(imageShape),
    latitude: PropTypes.number,
    longitude: PropTypes.number,
    name: PropTypes.string,
});

CarrierGuide.propTypes = {
    bestFeatures: PropTypes.shape({
        covidFeatures: PropTypes.arrayOf(PropTypes.object),
        generalFeatures: PropTypes.arrayOf(PropTypes.object),
    }),
    blurbSectionContent: PropTypes.shape({
        arriveCityImages: PropTypes.arrayOf(imageShape),
        arriveMainStationContent: PropTypes.string,
        arriveStationImages: PropTypes.arrayOf(imageShape),
        arriveStations: PropTypes.arrayOf(stationShape),
        blogPosts: PropTypes.arrayOf(
            PropTypes.shape({
                alt: PropTypes.string,
                author: PropTypes.string,
                src: PropTypes.string,
                title: PropTypes.string,
                url: PropTypes.string,
            })
        ),
        departCityImages: PropTypes.arrayOf(imageShape),
        departMainStationContent: PropTypes.string,
        departStationImages: PropTypes.arrayOf(imageShape),
        departStations: PropTypes.arrayOf(stationShape),
    }),
    carrier: shapes.carrierShape,
    carrierReviews: PropTypes.arrayOf(PropTypes.object),
    content: PropTypes.object.isRequired,
    infoSectionContent: PropTypes.arrayOf(
        PropTypes.shape({
            prefix: PropTypes.string,
            text: PropTypes.string,
            title: PropTypes.string,
        })
    ),
    interlinks: PropTypes.shape({
        c2cs: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        carriers: PropTypes.arrayOf(
            PropTypes.shape({
                carrierCode: PropTypes.string,
                carrierName: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
        cities: PropTypes.arrayOf(
            PropTypes.shape({
                linkAnchorText: PropTypes.string,
                linkUrl: PropTypes.string,
            })
        ),
    }),
    isFetching: PropTypes.bool.isRequired,
    locale: PropTypes.string.isRequired,
    longFormContent: PropTypes.object,
    minPriceUsd: PropTypes.number,
    quickviewMetrics: shapes.quickviewShape.isRequired,
    renderFlags: PropTypes.shape({
        showBlogBlurbSection: PropTypes.bool,
    }),
    reviewSummary: PropTypes.shape({
        overall: PropTypes.number,
        reviewCount: PropTypes.number,
        travelType: PropTypes.number,
    }),
    showModal: PropTypes.bool,
    stats: PropTypes.shape({
        cheapest: PropTypes.shape({
            routes: PropTypes.arrayOf(PropTypes.object),
        }),
        metrics: PropTypes.shape({
            cities: PropTypes.number,
            countries: PropTypes.number,
            routes: PropTypes.number,
            stations: PropTypes.number,
            tripsPerDay: PropTypes.number,
            tripsPerMonth: PropTypes.number,
        }),
        pricing: PropTypes.shape({
            maxUsd: PropTypes.number,
            minUsd: PropTypes.number,
        }),
        top: PropTypes.shape({
            cities: PropTypes.arrayOf(PropTypes.object),
            states: PropTypes.arrayOf(PropTypes.object),
        }),
    }),
    structuredData: PropTypes.arrayOf(PropTypes.object),
    t: PropTypes.func,
    topRoutes: PropTypes.arrayOf(
        PropTypes.shape({
            arrive_city: PropTypes.string,
            avg_duration_seconds: PropTypes.string,
            c2c_url_slug: PropTypes.string,
            depart_city: PropTypes.string,
            min_price_usd: PropTypes.string,
            trips_per_day: PropTypes.string,
        }),
    ),
    travelReviews: PropTypes.object,
};
CarrierGuide.defaultProps = {
    carrierReviews: [],
    infoSectionContent: [],
    interlinks: {},
    longFormContent: {},
    reviewSummary: {},
    showModal: false,
    stats: {
        metrics: {},
        pricing: {},
    },
    structuredData: [],
    travelReviews: {},
};

export {
    CarrierGuide,
};
export default localize(CarrierGuide, ['t'], cgConfig.namespace);
